import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Star,
    Trash2,
    Play,
    RefreshCw,
    Search,
    Inbox,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
    listSavedSearches,
    runSavedSearch,
    deleteSavedSearch,
    markSavedSearchSeen,
} from "@/lib/api";

function fmtRel(iso) {
    if (!iso) return "—";
    try {
        const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
        if (diff < 60) return "ahora mismo";
        if (diff < 3600) return `hace ${Math.round(diff / 60)} min`;
        if (diff < 86400) return `hace ${Math.round(diff / 3600)} h`;
        if (diff < 86400 * 7) return `hace ${Math.round(diff / 86400)} d`;
        return new Date(iso).toLocaleDateString("es-ES");
    } catch {
        return "—";
    }
}

function FilterChips({ s }) {
    const chips = [];
    if (s.q) chips.push({ k: "Texto", v: `"${s.q}"`, c: "violet" });
    if (s.cpv) chips.push({ k: "CPV", v: s.cpv, c: "sky" });
    if (s.contract_type) chips.push({ k: "Tipo", v: s.contract_type, c: "rose" });
    if (s.ambito) chips.push({ k: "Ámbito", v: s.ambito, c: "violet" });
    if (s.comunidad) chips.push({ k: "CA", v: s.comunidad, c: "emerald" });
    if (s.provincia) chips.push({ k: "Provincia", v: s.provincia, c: "amber" });
    if (s.municipio) chips.push({ k: "Municipio", v: s.municipio, c: "amber" });
    if (s.min_budget)
        chips.push({ k: "Mín", v: `${s.min_budget.toLocaleString("es-ES")}€`, c: "slate" });
    if (s.max_budget)
        chips.push({ k: "Máx", v: `${s.max_budget.toLocaleString("es-ES")}€`, c: "slate" });
    if (s.open_only) chips.push({ k: "Plazos abiertos", v: "", c: "emerald" });
    if (s.use_ai) chips.push({ k: "IA", v: "Claude", c: "fuchsia" });
    return (
        <div className="flex flex-wrap gap-1.5 mt-2">
            {chips.map((c, i) => (
                <span
                    key={`${c.k}-${i}`}
                    className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-${c.c}-50 text-${c.c}-700 border border-${c.c}-200`}
                >
                    {c.k}
                    {c.v ? `: ${c.v}` : ""}
                </span>
            ))}
        </div>
    );
}

export default function BusquedasGuardadas() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [runningId, setRunningId] = useState(null);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await listSavedSearches();
            setItems(d?.items || []);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudieron cargar las búsquedas",
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleRun = async (sid) => {
        setRunningId(sid);
        try {
            const updated = await runSavedSearch(sid);
            setItems((cur) =>
                cur.map((it) => (it.id === sid ? { ...it, ...updated } : it)),
            );
            const delta = updated.new_since_last_run || 0;
            toast.success(
                delta > 0
                    ? `${delta} nuevas licitaciones encontradas`
                    : "Búsqueda actualizada. Sin novedades.",
            );
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo ejecutar la búsqueda",
            );
        } finally {
            setRunningId(null);
        }
    };

    const handleDelete = async (sid, name) => {
        if (!window.confirm(`¿Eliminar la búsqueda "${name}"?`)) return;
        try {
            await deleteSavedSearch(sid);
            setItems((cur) => cur.filter((it) => it.id !== sid));
            toast.success("Búsqueda eliminada");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo eliminar la búsqueda",
            );
        }
    };

    const openInSearch = async (s) => {
        // Acknowledge new-count badge then jump to the search page with the
        // saved filters as query string. The Plcsp page already reads URL
        // params for `q` so this gives a "deep-link" UX.
        try {
            await markSavedSearchSeen(s.id);
        } catch {
            // best-effort
        }
        const params = new URLSearchParams();
        if (s.q) params.set("q", s.q);
        if (s.cpv) params.set("cpv", s.cpv);
        if (s.contract_type) params.set("contract_type", s.contract_type);
        if (s.ambito) params.set("ambito", s.ambito);
        if (s.comunidad) params.set("comunidad", s.comunidad);
        navigate(`/app/buscar-plcsp?${params.toString()}`);
    };

    return (
        <Layout>
            <div className="max-w-[1400px] mx-auto p-6 md:p-10 space-y-8">
                <header className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">
                            Mis búsquedas guardadas
                        </div>
                        <h1
                            className="font-display text-4xl md:text-5xl font-black tracking-tight leading-none"
                            data-testid="busquedas-title"
                        >
                            Búsquedas guardadas
                        </h1>
                        <p className="text-slate-600 mt-3 max-w-2xl">
                            Marca búsquedas con filtros recurrentes. Cada vez que
                            las ejecutes te diremos cuántas <b>nuevas</b>{" "}
                            licitaciones hay desde la última vez — todo en la
                            aplicación, sin emails.
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        className="rounded-xl border-slate-300"
                        onClick={() => navigate("/app/buscar-plcsp")}
                        data-testid="busquedas-new-btn"
                    >
                        <Search size={14} className="mr-2" />
                        Crear nueva
                    </Button>
                </header>

                {loading && (
                    <div className="grid gap-3">
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-32 w-full" />
                        ))}
                    </div>
                )}

                {!loading && items.length === 0 && (
                    <div
                        className="rounded-3xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-16 text-center"
                        data-testid="busquedas-empty"
                    >
                        <Inbox className="mx-auto text-slate-300 mb-4" size={56} />
                        <h2 className="font-display text-2xl font-semibold">
                            Aún no has guardado ninguna búsqueda
                        </h2>
                        <p className="text-slate-500 mt-2 max-w-md mx-auto">
                            En el buscador, configura los filtros que te
                            interesen y pulsa el botón{" "}
                            <span className="inline-flex items-center gap-1 align-middle">
                                <Star size={11} className="fill-amber-400 text-amber-500" />
                                <b>Guardar esta búsqueda</b>
                            </span>{" "}
                            para verla aquí.
                        </p>
                        <Button
                            className="rounded-xl bg-[#0055ff] hover:bg-[#0044cc] text-white mt-6"
                            onClick={() => navigate("/app/buscar-plcsp")}
                        >
                            Ir al buscador
                        </Button>
                    </div>
                )}

                {!loading && items.length > 0 && (
                    <div className="grid gap-3" data-testid="busquedas-list">
                        {items.map((s) => {
                            const isRunning = runningId === s.id;
                            const newCount = s.new_since_last_run || 0;
                            return (
                                <div
                                    key={s.id}
                                    className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-pop transition-all flex flex-col md:flex-row md:items-start gap-4"
                                    data-testid={`busquedas-row-${s.id}`}
                                >
                                    <div className="shrink-0 mt-1">
                                        <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center">
                                            <Star
                                                size={18}
                                                className="fill-amber-400 text-amber-500"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="font-display text-lg font-semibold text-slate-900">
                                                {s.name}
                                            </h3>
                                            {newCount > 0 && (
                                                <span
                                                    className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full bg-rose-500 text-white"
                                                    data-testid={`busquedas-badge-new-${s.id}`}
                                                >
                                                    +{newCount} NUEVAS
                                                </span>
                                            )}
                                        </div>
                                        <FilterChips s={s} />
                                        <div className="text-xs text-slate-500 mt-3 flex items-center gap-4 flex-wrap">
                                            <span>
                                                Total ahora:{" "}
                                                <b className="text-slate-800 font-mono">
                                                    {(s.current_total ?? 0).toLocaleString("es-ES")}
                                                </b>
                                            </span>
                                            <span>
                                                Última ejecución:{" "}
                                                <b className="text-slate-700">
                                                    {fmtRel(s.last_run_at)}
                                                </b>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex flex-row md:flex-col gap-2 shrink-0">
                                        <Button
                                            size="sm"
                                            className="rounded-xl bg-[#0055ff] hover:bg-[#0044cc] text-white"
                                            onClick={() => openInSearch(s)}
                                            data-testid={`busquedas-open-${s.id}`}
                                        >
                                            <Search size={13} className="mr-1.5" />
                                            Ver resultados
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="rounded-xl"
                                            disabled={isRunning}
                                            onClick={() => handleRun(s.id)}
                                            data-testid={`busquedas-run-${s.id}`}
                                        >
                                            {isRunning ? (
                                                <RefreshCw
                                                    size={13}
                                                    className="mr-1.5 animate-spin"
                                                />
                                            ) : (
                                                <Play size={13} className="mr-1.5" />
                                            )}
                                            Refrescar
                                        </Button>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(s.id, s.name)}
                                            title="Eliminar búsqueda"
                                            className="flex-1 rounded-xl h-8 px-3 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 transition-all inline-flex items-center justify-center gap-1.5 text-xs font-bold"
                                            data-testid={`busquedas-delete-${s.id}`}
                                        >
                                            <Trash2 size={12} />
                                            Eliminar
                                        </button>
                                    </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 text-xs text-slate-600 leading-relaxed">
                    <strong className="text-slate-800">¿Cómo funciona?</strong>{" "}
                    Cada vez que abras esta página o pulses <b>Refrescar</b>, el
                    sistema recalcula cuántas licitaciones nuevas hay desde la
                    última vez. Si hay novedades verás el badge rojo{" "}
                    <span className="inline-flex items-center gap-1 align-middle">
                        <span className="px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[9px] font-black">
                            +N NUEVAS
                        </span>
                    </span>{" "}
                    hasta que las marques como vistas pulsando <b>Ver
                    resultados</b>.
                </div>
            </div>
        </Layout>
    );
}
